import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/Button";
import { useConfirm } from "@/components/ConfirmDialog";
import { LoadError } from "@/components/LoadError";
import { SetupStep } from "@/components/SetupStep";
import { StatusPill } from "@/components/StatusPill";
import { TextInput } from "@/components/TextInput";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { isConfigured, isEnabled } from "@/lib/enums";
import { formatDateTime } from "@/lib/formatDateTime";
import { SettingsSection, SettingsSkeleton } from "../settings/SettingsSection";

interface ClaudeStatus {
  status: number;
  requestsPerMinute: number | null;
  enabledAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  streamContent: boolean;
  logStreamingEnabled: boolean;
}

const DEFAULT_REQUESTS_PER_MINUTE = 300;
const MIN_REQUESTS_PER_MINUTE = 30;
const MAX_REQUESTS_PER_MINUTE = 600;

export function ClaudeConnection() {
  const api = useApi();
  const { orgId } = useParams();
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [status, setStatus] = useState<ClaudeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [requestsPerMinute, setRequestsPerMinute] = useState(
    String(DEFAULT_REQUESTS_PER_MINUTE),
  );

  const fetchStatus = useCallback(
    () =>
      orgId
        ? api.get<ClaudeStatus>(`/organizations/${orgId}/claude`)
        : Promise.resolve(null),
    [api, orgId],
  );

  const applyStatus = useCallback((data: ClaudeStatus | null) => {
    if (data) {
      setStatus(data);
      setError(null);
    } else {
      setError("Couldn't load the Claude integration.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchStatus().then((data) => {
      if (!cancelled) applyStatus(data);
    });

    return () => {
      cancelled = true;
    };
  }, [fetchStatus, applyStatus]);

  async function run(
    action: () => Promise<void>,
    success: string,
    failure: string,
  ) {
    setSubmitting(true);
    setError(null);

    try {
      await action();
      applyStatus(await fetchStatus());
      showToast(success, "success");
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit() {
    setAccessKey("");
    setRequestsPerMinute(
      String(status?.requestsPerMinute ?? DEFAULT_REQUESTS_PER_MINUTE),
    );
    setError(null);
    setIsEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    if (!orgId) return;

    const connecting = !connected;

    await run(
      async () => {
        await api.send(
          `/organizations/${orgId}/claude`,
          "POST",
          {
            accessKey: accessKey || null,
            requestsPerMinute: Number(requestsPerMinute),
          },
          "Couldn't save the Claude integration.",
        );

        if (connecting) {
          await api.send(
            `/organizations/${orgId}/claude/enable`,
            "PATCH",
            undefined,
            "Couldn't start recording.",
          );
        }

        setAccessKey("");
        setIsEditing(false);
      },
      connecting
        ? "Claude connected. Recording has started."
        : "Claude integration saved.",
      "Couldn't save the Claude integration.",
    );
  }

  async function setRecording(enabled: boolean) {
    if (!orgId) return;

    await run(
      () =>
        api.send(
          `/organizations/${orgId}/claude/${enabled ? "enable" : "disable"}`,
          "PATCH",
          undefined,
          enabled ? "Couldn't resume recording." : "Couldn't pause recording.",
        ),
      enabled ? "Recording resumed." : "Recording paused.",
      enabled ? "Couldn't resume recording." : "Couldn't pause recording.",
    );
  }

  async function setContentStreaming(enabled: boolean) {
    if (!orgId) return;

    await run(
      () =>
        api.send(
          `/organizations/${orgId}/claude/content-streaming/${enabled ? "enable" : "disable"}`,
          "PATCH",
          undefined,
          "Couldn't change content streaming.",
        ),
      enabled
        ? "Conversation content will be sent to the log streaming destination."
        : "Stopped sending conversation content to the log streaming destination.",
      "Couldn't change content streaming.",
    );
  }

  async function handleDisconnect() {
    if (!orgId) return;

    const confirmed = await confirm(
      "Stops recording and deletes the key. Existing recordings remain until they expire under Tendra's retention policy.",
      { title: "Disconnect Claude", confirmLabel: "Disconnect" },
    );

    if (!confirmed) return;

    await run(
      () =>
        api.send(
          `/organizations/${orgId}/claude`,
          "DELETE",
          undefined,
          "Couldn't disconnect Claude.",
        ),
      "Claude disconnected.",
      "Couldn't disconnect Claude.",
    );
  }

  const connected = status !== null && isConfigured(status.status);
  const recording = status !== null && isEnabled(status.status);
  const showForm = !connected || isEditing;

  return (
    <SettingsSection
      className="text-foreground"
      title="Claude"
      description="Record your organization's Claude conversations using Anthropic's compliance API."
    >
      {!status && error ? (
        <LoadError
          what="the Claude integration"
          onRetry={() => void fetchStatus().then(applyStatus)}
        />
      ) : !status ? (
        <SettingsSkeleton steps={2} />
      ) : (
        <>
          {error && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {error}
            </p>
          )}

          <div className="rounded-lg bg-surface p-4">
            <ol className="list-none">
              <SetupStep
                label="1"
                state={showForm ? "current" : "done"}
                title="Connect Claude"
                last
              >
                {showForm ? (
                  <form onSubmit={handleSave} className="mt-3 space-y-5">
                    <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                      In claude.ai, create a compliance access key (
                      <a
                        href="https://support.claude.com/en/articles/13015708-access-the-compliance-api"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        see Anthropic's guide
                      </a>
                      ) with only this scope:
                      <code className="mt-1.5 block w-fit rounded-md border border-accent/40 bg-accent/10 px-2 py-1 font-mono text-xs text-accent">
                        read:compliance_user_data
                      </code>
                    </p>
                    <div>
                      <label
                        htmlFor="claudeAccessKey"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Compliance access key
                      </label>
                      <TextInput
                        id="claudeAccessKey"
                        type="password"
                        mono
                        value={accessKey}
                        onChange={(e) => setAccessKey(e.target.value)}
                        placeholder={connected ? "················" : undefined}
                        required={!connected}
                        autoComplete="off"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="claudeRequestsPerMinute"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Request limit
                      </label>
                      <div className="flex items-center gap-2.5">
                        <TextInput
                          id="claudeRequestsPerMinute"
                          type="number"
                          min={MIN_REQUESTS_PER_MINUTE}
                          max={MAX_REQUESTS_PER_MINUTE}
                          value={requestsPerMinute}
                          onChange={(e) => setRequestsPerMinute(e.target.value)}
                          required
                          aria-describedby="claudeRequestsPerMinuteHint"
                          className="w-24 tabular-nums"
                        />
                        <span className="text-sm text-foreground/70">
                          requests per minute
                        </span>
                      </div>
                      <p
                        id="claudeRequestsPerMinuteHint"
                        className="mt-1.5 text-xs leading-relaxed text-foreground/70"
                      >
                        Anthropic limits your organization to 600 requests per
                        minute, shared across all tools using the Compliance
                        API. Lower limits leave capacity for other tools but can
                        delay new recordings. In a busy organization, 60
                        requests per minute may mean a delay of about 10
                        minutes; 300, about 2 minutes. The minimum is{" "}
                        {MIN_REQUESTS_PER_MINUTE}.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? "Saving..." : connected ? "Save" : "Save"}
                      </Button>
                      {isEditing && (
                        <Button
                          onClick={() => setIsEditing(false)}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                ) : (
                  <>
                    <dl className="mt-1 space-y-1">
                      <div>
                        <dt className="inline text-sm text-foreground/70">
                          Compliance access key:{" "}
                        </dt>
                        <dd className="inline text-sm">················</dd>
                      </div>
                      <div>
                        <dt className="inline text-sm text-foreground/70">
                          Request limit:{" "}
                        </dt>
                        <dd className="inline text-sm tabular-nums">
                          {status.requestsPerMinute} requests per minute
                        </dd>
                      </div>
                    </dl>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                      {status.lastError ? (
                        <>
                          <StatusPill variant="suspended">
                            Recording stopped
                          </StatusPill>
                          <span className="text-foreground/70">
                            {status.lastError}
                          </span>
                        </>
                      ) : recording && status.enabledAt ? (
                        <>
                          <StatusPill variant="active">Recording</StatusPill>
                          <span className="text-foreground/70">
                            Since {formatDateTime(status.enabledAt)}
                          </span>
                        </>
                      ) : (
                        <StatusPill variant="idle">Paused</StatusPill>
                      )}
                    </div>
                    <div className="mt-3">
                      <Button onClick={startEdit} disabled={submitting}>
                        Edit
                      </Button>
                    </div>
                  </>
                )}
              </SetupStep>
            </ol>
          </div>

          {connected && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between gap-6 rounded-lg border border-muted p-4">
                <div>
                  <h3 className="text-sm font-semibold">
                    Stream conversation content
                  </h3>
                  <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                    Each prompt and response is sent to your log streaming
                    destination as it's recorded, letting you retain it beyond
                    Tendra's retention policy. Messages larger than 64 KB are
                    sent in parts.
                  </p>
                </div>
                <span
                  title={
                    !status.logStreamingEnabled && !status.streamContent
                      ? "Enable log streaming first"
                      : undefined
                  }
                  className="shrink-0"
                >
                  <Button
                    onClick={() =>
                      void setContentStreaming(!status.streamContent)
                    }
                    disabled={
                      submitting ||
                      (!status.logStreamingEnabled && !status.streamContent)
                    }
                  >
                    {status.streamContent ? "Disable" : "Enable"}
                  </Button>
                </span>
              </div>

              {!status.lastError && (
                <div className="flex items-center justify-between gap-6 rounded-lg border border-muted p-4">
                  <div>
                    <h3 className="text-sm font-semibold">
                      {recording ? "Pause recording" : "Resume recording"}
                    </h3>
                    <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                      Nothing is recorded while paused, and missed activity
                      isn't captured when you resume.
                    </p>
                  </div>
                  <Button
                    onClick={() => void setRecording(!recording)}
                    disabled={submitting}
                    className="shrink-0"
                  >
                    {recording ? "Pause" : "Resume"}
                  </Button>
                </div>
              )}

              <div className="flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
                <div>
                  <h3 className="text-sm font-semibold">Disconnect Claude</h3>
                  <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                    Stops recording and deletes the key. Existing recordings
                    remain until they expire under Tendra's retention policy.
                  </p>
                </div>
                <Button
                  variant="dangerOutline"
                  onClick={handleDisconnect}
                  disabled={submitting}
                  className="shrink-0"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {confirmDialog}
      <Toast toast={toast} />
    </SettingsSection>
  );
}
